import React, { useState, useEffect, useRef, useContext } from 'react';
import _ from 'lodash';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button } from '@telus-uds/components-web';
import { HeaderButton, HeaderLinkText } from './css';
import ConfirmationBox from './shared/ConfirmationBox';
import { useLocaleActionsContext } from '../context/LocaleContext';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import request from '../utils/request';
import Help from '../help';
// eslint-disable-next-line import/no-cycle
import { GlobalContext } from './layout';
import {
  getContentfulData,
  contentfulFormConfig,
  getData
} from '../contentful';
import { localeEn, localeFr, getPathVariables } from '../misc';

const Menu = ({ locale, userRights }) => {
  const history = useHistory();
  const { defectNotificationCount } = useContext(GlobalContext);
  const setLocale = useLocaleActionsContext();
  const localizedStrings = useLocalizedStringsContext();
  const [isModalOpen, setModalOpen] = useState(false);
  const [showHelpSection, setShowHelpSection] = useState(false);

  const config = {
    ...contentfulFormConfig,
    content_type: 'header',
    locale
  };
  const [helpLink, setHelpLink] = useState('');
  const {
    headerLinks: {
      home,
      help,
      labelEnglish,
      labelFrench,
      labelLogout,
      labelLogDefect
    },
    switchLocaleModal: {
      modalTitle,
      modalBody,
      modalConfirmButtonLabel,
      modalCancelButtonLabel
    }
  } = localizedStrings;

  const onClickLogout = () => {
    request
      .get('/orders/unlock')
      .then(() => {
        localStorage.clear();
        history.push('/');
        window.location.reload();
      })
      .catch(err => {
        throw err;
      });
  };

  const changeLocale = () => {
    setLocale(locale === localeEn ? localeFr : localeEn);
  };

  const shouldSwitchLocale = () => {
    const [, path] = getPathVariables(history.location.pathname);
    if (path) setModalOpen(true);
    else changeLocale();
  };

  const onConfirm = () => {
    setModalOpen(false);
    changeLocale();
    history.push('/');
  };

  const onCloseModal = () => setModalOpen(false);

  useEffect(() => {
    getContentfulData(config)
      .then(res => {
        const headerData = getData(res);
        const bconLink = _.get(headerData, 'helpPdf.fields.file.url', '');
        const bussAppLink = _.get(
          headerData,
          'businessAppsHelpPdf.fields.file.url',
          ''
        );
        const iotLink = _.get(headerData, 'ioTHelpPdf.fields.file.url', '');
        setHelpLink({ bconLink, bussAppLink, iotLink });
      })
      .catch(() => {});
  }, [locale]);

  const focusElementOnClose = useRef(null);

  return (
    <>
      {userRights && (
        <>
          {userRights.isHomeLinkVisible && (
            <li>
              <div>
                <Link variant="link" to="/">
                  <HeaderLinkText>{home}</HeaderLinkText>
                </Link>
              </div>
            </li>
          )}
          {userRights.isHelpLinkVisible && (
            <li>
              <div>
                <HeaderButton onClick={() => setShowHelpSection(true)}>
                  {help}
                </HeaderButton>
              </div>
            </li>
          )}
          {userRights.isReportAnIssueVisible && (
            <li>
              <div>
                <Link
                  style={{ display: 'flex' }}
                  variant="link"
                  to="/report-an-issue"
                >
                  <HeaderLinkText>{labelLogDefect}</HeaderLinkText>
                  {defectNotificationCount > 0 ? (
                    <sup>
                      <span className="badge badge-pill badge-danger">
                        {defectNotificationCount}
                      </span>
                    </sup>
                  ) : (
                    ''
                  )}
                </Link>
              </div>
            </li>
          )}

          <li>
            <div>
              <HeaderButton onClick={shouldSwitchLocale}>
                {locale === localeEn ? labelFrench : labelEnglish}
              </HeaderButton>
            </div>
          </li>
          <li>
            <div>
              <HeaderButton
                style={{ borderRight: 'none' }}
                onClick={onClickLogout}
              >
                {labelLogout}
              </HeaderButton>
            </div>
          </li>
        </>
      )}
      <ConfirmationBox
        open={isModalOpen}
        hideCloseButton={!isModalOpen}
        setOpen={() => isModalOpen}
        header={modalTitle}
        onClose={onCloseModal}
      >
        <p style={{ fontSize: '15px', padding: '8px 0' }}>{modalBody}</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
          <Button variant={{ priority: 'high' }} onPress={onConfirm}>
            {modalConfirmButtonLabel}
          </Button>
          <Button variant={{ priority: 'low' }} onPress={onCloseModal}>
            {modalCancelButtonLabel}
          </Button>
        </div>
      </ConfirmationBox>
      {showHelpSection && (
        <Help guideLink={helpLink} setShowHelpSection={setShowHelpSection} />
      )}
    </>
  );
};

Menu.propTypes = {
  locale: PropTypes.string.isRequired,
  userRights: PropTypes.shape({
    isHelpLinkVisible: PropTypes.bool,
    isReportAnIssueVisible: PropTypes.bool,
    isStatusEditable: PropTypes.bool,
    isUserEditable: PropTypes.bool,
    canAssignOrder: PropTypes.bool,
    isAdminBandVisible: PropTypes.bool,
    isUserPageVisible: PropTypes.bool,
    isSOVDashboardVisible: PropTypes.bool,
    isQuoteApprovalDashboardVisible: PropTypes.bool,
    isBulkUploadVisible: PropTypes.bool,
    isHomeLinkVisible: PropTypes.bool
  }).isRequired
};

export default Menu;
